import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { googleCallback } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { apiErrorCode, authErrorMessage } from '../auth/errors';
import { GOOGLE_FLOW_KEY } from '../auth/google';

export function GoogleCallbackPage() {
  const { t } = useTranslation(['auth', 'common', 'errors']);
  const { applyUser } = useAuth();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const params = new URLSearchParams(window.location.search);
    // The authorization code must not stay in history or be re-submitted on refresh.
    window.history.replaceState(null, '', window.location.pathname);
    const code = params.get('code');
    const state = params.get('state');
    let flow: { binding: string; purpose: 'login' | 'link' } | null = null;
    try {
      flow = JSON.parse(sessionStorage.getItem(GOOGLE_FLOW_KEY) || 'null');
    } catch {
      flow = null;
    }
    sessionStorage.removeItem(GOOGLE_FLOW_KEY);
    if (params.get('error')) {
      setError(t('auth:googleCancelled'));
      return;
    }
    if (!code || !state || !flow) {
      setError(t('auth:errors.invalid_state'));
      return;
    }
    const purpose = flow.purpose;
    void googleCallback({ code, state, binding: flow.binding })
      .then((res) => {
        if (res.session) {
          applyUser(res.session.user);
          navigate('/', { replace: true });
        } else if (res.user) {
          applyUser(res.user);
          navigate('/security', { replace: true, state: { notice: 'googleLinked' } });
        }
      })
      .catch((err) => {
        setConflict(purpose === 'login' && apiErrorCode(err) === 'google_account_exists_link_required');
        setError(authErrorMessage(t, err));
      });
  }, [applyUser, navigate, t]);

  return (
    <div className="page auth-page auth-sheet-page">
      <article className="auth-card auth-sheet">
        <h1>Google</h1>
        {error ? (
          <>
            <p className="auth-error" role="alert">
              {error}
            </p>
            <Link to="/auth/login" className="btn btn-primary btn-block">
              {conflict ? t('auth:signInToLink') : t('auth:backToLogin')}
            </Link>
          </>
        ) : (
          <p className="muted-note" role="status">
            {t('auth:googleWorking')}
          </p>
        )}
      </article>
    </div>
  );
}
